import type { UserRole } from '@lmc/ts-common'
import { EmptyState, NoDataIcon, PageHeader } from '@lmc/ui-kit'
import { Button, Text } from '@lmc/ui-kit/src/general'
import type { ColumnsType } from 'antd/lib/table'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import { reportingConstants } from 'locale/reporting'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import AuditFilters from './components/AuditFilters'
import AuditTable from './components/AuditTable'
import { ColumnContainer, SubText } from './components/styledComponents'
import useAuditTrail from './hooks/useAuditTrail'
import {
  type AuditTableDataSource,
  type AuditTrailFilters,
  getDetails,
  getFullEvent
} from './utils/utils'

const initialFiltersValue: AuditTrailFilters = {
  rangePicker: [dayjs().subtract(1, 'month'), dayjs()],
  agentId: [],
  pagination: { page: 1, sort: -1 },
  changeType: undefined
}

export const AuditTrailScreen = () => {
  const { t } = useTranslation(NAMESPACE.REPORTING)
  const [filterParams, setFilterParams] = useState(initialFiltersValue)
  const [currentPage, setCurrentPage] = useState(1)
  const [sortDirection, setSortDirection] = useState<'ascend' | 'descend'>(
    'descend'
  )

  const { auditLogs, pagination, isLoading } = useAuditTrail({
    from: dayjs(filterParams.rangePicker?.[0]).startOf('day').toISOString(),
    to: dayjs(filterParams.rangePicker?.[1]).endOf('day').toISOString(),
    agentId: filterParams.agentId,
    pagination: {
      page: currentPage,
      sort: { recorded: sortDirection === 'ascend' ? 1 : -1 }
    },
    changeType: filterParams.changeType
  })
  const carePlanNominative = useSelector(
    (state: { global: { home: { preferredCarePlansNominative: string } } }) =>
      state.global.home.preferredCarePlansNominative || ''
  )
  const residentNominative = useSelector(
    (state: { global: { home: { preferredPeopleNominative: string } } }) =>
      state.global.home.preferredPeopleNominative || ''
  )

  const columns: ColumnsType<AuditTableDataSource> = [
    {
      dataIndex: 'dateTime',
      width: 140,
      title: (
        <ColumnContainer>
          <Text weight="medium">{t(reportingConstants.DATE_TIME)}</Text>
          <Text weight="medium">{t(reportingConstants.TIMEZONE)}</Text>
        </ColumnContainer>
      ),
      key: 'recorded',
      sorter: true,
      render: (dateTime: string) => (
        <ColumnContainer>
          <Text>{dayjs(dateTime).format('DD-MM-YYYY')}</Text>
          <Text>{dayjs(dateTime).format('HH:mm:ss')}</Text>
        </ColumnContainer>
      ),
      sortOrder: sortDirection,
      showSorterTooltip: false,
      onHeaderCell: () => ({
        onClick: () => {
          const newDirection = sortDirection === 'ascend' ? 'descend' : 'ascend'
          setSortDirection(newDirection)
        }
      })
    },
    {
      dataIndex: 'author',
      width: 192,
      title: <Text weight="medium">{t(reportingConstants.AUTHOR)}</Text>,
      key: 'author',
      render: (author: string, record: { authorId?: string }) => {
        return (
          <ColumnContainer>
            <Text>{author}</Text>
            <SubText level={2}>{record.authorId}</SubText>
          </ColumnContainer>
        )
      }
    },
    {
      dataIndex: 'role',
      width: 128,
      title: <Text weight="medium">{t(reportingConstants.ROLE)}</Text>,
      key: 'author',
      render: (role: UserRole) => {
        return <Text>{t(reportingConstants[role])}</Text>
      }
    },
    {
      dataIndex: 'event',
      width: 144,
      title: <Text weight="medium">{t(reportingConstants.EVENT)}</Text>,
      key: 'event',
      render: (event: string, record) =>
        getFullEvent(event, record, carePlanNominative, residentNominative)
    },
    {
      dataIndex: 'details',
      title: <Text weight="medium">{t(reportingConstants.DETAILS)}</Text>,
      key: 'details',
      render: (details) =>
        getDetails(t, details, carePlanNominative, residentNominative)
    }
  ]

  const dataSource: AuditTableDataSource[] = auditLogs?.map((auditLog) => ({
    dateTime: auditLog.recorded.toString(),
    author: auditLog?.agent?.name,
    authorId: auditLog.agent?.who,
    role: auditLog?.agent?.role,
    event: `${auditLog?.action}.${auditLog.changeType}`,
    entities: auditLog.entities,
    details: auditLog
  }))

  const isStartDateOlderThanOneMonth = dayjs(
    filterParams.rangePicker?.[0]
  ).isBefore(dayjs().subtract(1, 'month').startOf('day'))

  return (
    <Wrapper>
      <PageHeader title={t(reportingConstants.AUDIT_TITLE)} />
      <AuditFilters
        onChange={setFilterParams}
        value={filterParams}
        setCurrentPage={setCurrentPage}
      />
      {isStartDateOlderThanOneMonth ? (
        <EmptyState
          title={t(reportingConstants.ARCHIVED_DATA)}
          description={
            <DescriptionContainer>
              <Description>
                {t(reportingConstants.ARCHIVED_DATA_DESCRIPTION_I)}
              </Description>
              <Button
                variant="text"
                onClick={() => {
                  if (window.Intercom) window.Intercom('showNewMessage')
                }}
              >
                {t(reportingConstants.ARCHIVED_DATA_DESCRIPTION_II)}
              </Button>
            </DescriptionContainer>
          }
          icon={<NoDataIcon />}
        />
      ) : (
        <AuditTable
          columns={columns}
          dataSource={dataSource}
          hasLogs={auditLogs?.length > 0}
          isLoading={isLoading}
          count={pagination?.totalItems || 0}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      )}
    </Wrapper>
  )
}

const DescriptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const Description = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
  text-align: center;
`

const Wrapper = styled.div`
  max-width: 1480px;
  padding: 24px 0 50px;
  margin: 0 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`
